// Featured QA Item Component
// Displays Q&A items in the featured section; answer is markdown with clamped height.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { LearnQaAnswerMarkdown } from './LearnQaAnswerMarkdown';
import './FeaturedQAItem.css';

const OVERFLOW_EPSILON = 2;

export function FeaturedQAItem({
  qa,
  onQAClick
}) {
  const answerClipRef = useRef(null);
  const [answerOverflows, setAnswerOverflows] = useState(false);

  const measureAnswerOverflow = useCallback(() => {
    const el = answerClipRef.current;
    if (!el) {
      setAnswerOverflows(false);
      return;
    }
    setAnswerOverflows(el.scrollHeight > el.clientHeight + OVERFLOW_EPSILON);
  }, []);

  useEffect(() => {
    const clip = answerClipRef.current;
    if (!clip || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const ro = new ResizeObserver(() => measureAnswerOverflow());
    ro.observe(clip);
    const inner = clip.firstElementChild;
    if (inner) {
      ro.observe(inner);
    }
    return () => ro.disconnect();
  }, [qa, measureAnswerOverflow]);

  const handleCardClick = (e) => {
    if (!onQAClick) return;
    if (e.target.closest('a')) return;
    onQAClick(qa.id);
  };

  const handleCardKeyDown = (e) => {
    if (!onQAClick) return;
    if (e.key !== 'Enter' && e.key !== ' ') return;
    if (e.target.closest('a')) return;
    e.preventDefault();
    onQAClick(qa.id);
  };

  const answerBodyClass = [
    'featured-qa-answer',
    'featured-qa-answer-body',
    'learn-qa-answer-markdown-host',
    'learn-qa-answer-markdown-host--featured',
    answerOverflows ? 'featured-qa-answer-body--overflows' : ''
  ].filter(Boolean).join(' ');

  return (
    <div
      className={`featured-qa-item${onQAClick ? ' featured-qa-item--clickable' : ''}`}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role={onQAClick ? 'button' : undefined}
      tabIndex={onQAClick ? 0 : undefined}
      aria-label={onQAClick ? `Open question: ${qa.question}` : undefined}
    >
      <div className="featured-qa-header">
        <div className="featured-qa-header-left">
          <h3 className="featured-qa-question">{qa.question}</h3>
        </div>
      </div>
      <div ref={answerClipRef} className={answerBodyClass}>
        <LearnQaAnswerMarkdown qa={qa} onContentRendered={measureAnswerOverflow} />
      </div>
      <div className="featured-qa-footer">
        {/* Tags */}
        {qa.tags && qa.tags.length > 0 && (
          <div className="featured-qa-tags">
            {qa.tags.map((tag, index) => (
              <span key={index} className="featured-qa-tag">{tag}</span>
            ))}
          </div>
        )}

        {/* Spacer to push likes to right when no tags */}
        {(!qa.tags || qa.tags.length === 0) && (
          <div className="featured-qa-spacer"></div>
        )}

        {/* Likes */}
        <div className="featured-qa-likes">
          <i className="fa-solid fa-thumbs-up"></i>
          <span>{qa.helpful || 0}</span>
        </div>
      </div>
    </div>
  );
}
